import React, { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template23: Portada especial para guías precargadas (Kioto, Tokio, etc.)
// Copia DIRECTA del componente Page1.jsx de guia-kioto, adaptado para usar las props del sistema guia
// Usa las imágenes desde public/images/ en lugar de imports relativos
const Template23 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const [logoError, setLogoError] = useState(false);
	const [portadaError, setPortadaError] = useState(false);
	const [backgroundStickersError, setBackgroundStickersError] = useState(false);
	const [backgroundError, setBackgroundError] = useState(false);

	const prevPageRef = useRef(page);
	useEffect(() => {
		if (prevPageRef.current !== page) {
			setLogoError(false);
			setPortadaError(false);
			setBackgroundStickersError(false);
			setBackgroundError(false);
			prevPageRef.current = page;
		}
	}, [page]);

	// Obtener las imágenes desde page.images y page.canvasImages
	const backgroundImage = page.images?.[0]?.src;
	const backgroundStickersImage = page.canvasImages?.find(
		(img) => img.id === "background-stickers",
	)?.imageUrl;
	const portadaImage = page.canvasImages?.find(
		(img) => img.id === "portada-central",
	)?.imageUrl;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	const headerText = page.texts?.[0]?.content;

	// Extraer texto y colores del SVG guardado en page.texts[1]
	const parseSVGText = () => {
		const svgContent = page.texts?.[1]?.content;

		// Parsear el SVG para extraer los textos y colores
		if (!svgContent)
			return {
				text1: "",
				text2: "",
				fill1: "",
				stroke1: "",
				fill2: "",
				stroke2: "",
			};
		const parser = new DOMParser();
		const doc = parser.parseFromString(svgContent, "image/svg+xml");
		const texts = doc.querySelectorAll("text");

		const text1 = texts[0]?.textContent?.trim();
		const text2 = texts[1]?.textContent?.trim();
		const fill1 = texts[0]?.getAttribute("fill");
		const stroke1 = texts[0]?.getAttribute("stroke");
		const fill2 = texts[1]?.getAttribute("fill");
		const stroke2 = texts[1]?.getAttribute("stroke");

		return { text1, text2, fill1, stroke1, fill2, stroke2 };
	};

	const { text1, text2, fill1, stroke1, fill2, stroke2 } = parseSVGText();

	// Componente exacto de Page1.jsx, sin framer-motion y usando las imágenes del sistema
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				width: "100%",
				height: "100%",
				backgroundColor,
				borderRadius: "12px",
				overflow: "hidden",
				position: "relative",
				"& p": {
					marginTop: "0px !important",
					marginBottom: "10px !important",
				},
				"& *:not(text)": {
					fontFamily: `${fontFamily} !important`,
				},
			}}
		>
			{/* CABECERA */}
			<div
				data-block-id="header"
				style={{
					backgroundColor: headerBackgroundColor,
					color: headerTextColor,
					textAlign: "center",
					padding: "15px 0",
					fontFamily,
					fontSize: "14px",
					fontWeight: 800,
				}}
			>
				{headerText}
			</div>
			{logoImage && !logoError && (
				<img
					src={logoImage}
					alt=""
					onError={() => setLogoError(true)}
					style={{
						width: 100,
						position: "absolute",
						right: 0,
						top: 14,
						zIndex: 99999,
					}}
				/>
			)}

			{/* CONTENIDO CENTRAL */}
			<div
				data-block-id="image-0"
				style={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					padding: 30,
					textAlign: "center",
					position: "relative",
				}}
			>
				{portadaImage && !portadaError ? (
					<img
						src={portadaImage}
						alt="portada"
						onError={() => setPortadaError(true)}
						style={{
							width: 300,
							height: 300,
							objectFit: "contain",
							zIndex: 101,
						}}
					/>
				) : (
					<ImagenNoDisponibleKawaii
						width={300}
						height={300}
						fontFamily={fontFamily}
					/>
				)}
				<div
					data-block-id="text-1"
					style={{
						textAlign: "center",
						zIndex: 102,
						marginTop: "-55px",
						overflow: "hidden",
						width: "100%",
						height: "180px",
						position: "relative",
					}}
				>
					<svg
						width="100%"
						height="180"
						viewBox="0 0 600 180"
						preserveAspectRatio="xMidYMid meet"
						style={{ fontFamily: "'Titan One', cursive", display: "block" }}
					>
						<text
							x="300"
							y="75"
							textAnchor="middle"
							dominantBaseline="middle"
							fontFamily="'Titan One', cursive"
							fontSize="80"
							fill={fill1}
							stroke={stroke1}
							strokeWidth="30"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeMiterlimit="10"
							paintOrder="stroke"
						>
							{text1}
						</text>
						<text
							x="300"
							y="125"
							textAnchor="middle"
							dominantBaseline="middle"
							fontFamily="'Titan One', cursive"
							fontSize="40"
							fill={fill2}
							stroke={stroke2}
							strokeWidth="20"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeMiterlimit="10"
							paintOrder="stroke"
						>
							{text2}
						</text>
					</svg>
				</div>
				{backgroundStickersImage && !backgroundStickersError && (
					<img
						src={backgroundStickersImage}
						alt=""
						onError={() => setBackgroundStickersError(true)}
						style={{
							width: "100%",
							height: "100%",
							position: "absolute",
							top: 0,
							right: 0,
							bottom: 0,
							left: 0,
							objectFit: "cover",
							zIndex: 100,
						}}
					/>
				)}
				<div
					style={{
						width: "100%",
						height: "100%",
						position: "absolute",
						top: 0,
						right: 0,
						bottom: 0,
						left: 0,
						objectFit: "cover",
						backgroundColor: "black",
						zIndex: 50,
						opacity: 0.4,
					}}
				></div>
				{backgroundImage && !backgroundError ? (
					<img
						src={backgroundImage}
						alt=""
						onError={() => setBackgroundError(true)}
						style={{
							width: "100%",
							height: "100%",
							position: "absolute",
							top: 0,
							right: 0,
							bottom: 0,
							left: 0,
							objectFit: "cover",
						}}
					/>
				) : null}
			</div>
		</Box>
	);
};

Template23.thumbnail = "/images/template-1.png";

export default Template23;
